/**
 * Punkt 121, Stufe 4 (docs/KONZEPT-LEAD-HERKUNFT.md) — „Wie kommen mehr Anfragen rein?“.
 * Antoine, 25.09.2026: „Die KI soll immer analysieren, wie man das verbessern kann, dass mehr
 * reinkommt.“
 *
 * Seit Punkt 124 (26.09.2026) auf der EINEN Seite „Auswertung“ und der EINZIGE KI-Text dort:
 * das frühere Business-Health-Fazit ist darin aufgegangen (Entscheidung 5), im selben
 * 5-€-Monatsdeckel, mit Belegpflicht. Der Knopf analysiert den Zeitraum, den die Seite gerade
 * zeigt. Hier steht die gespeicherte letzte Analyse mit Zeitpunkt und den Zahlen, auf die sie sich
 * stützt. Text und Zahlen kommen fertig aus `/api/lead-analyse`.
 */
import { useState } from "react";
import { antwortDauer, zeitraumText, type EingangsAntwortzeit, type EingangsStatistik, type Zeitraum } from "@maestro/auswertung";
import { useApi } from "../hooks/useApi";
import { apiFetch, toApiError } from "../lib/api";
import { formatDateTime, money } from "./format";
import { Button, ErrorNote, Icon, LoadingRows } from "./components";

export interface LeadAnalyse {
  id: string;
  erstelltAm: string;
  ausloeser: "knopf" | "monatlich";
  status: "fertig" | "verworfen";
  text: string | null;
  zahlen: EingangsStatistik | null;
}

export interface LeadAnalyseAntwort {
  letzte: LeadAnalyse | null;
  kostenMonatCent: number;
  deckelCent: number;
  gesperrt: boolean;
}

const TEXT = "font-body-md text-body-md leading-relaxed text-on-background dark:text-inverse-on-surface";
const KLEIN = "font-body-sm text-[12px] leading-relaxed text-secondary dark:text-secondary-fixed-dim";

/** Absatz + Spiegelstrich-Zeilen (Format aus dem Prompt, lib/lead-analyse.ts). */
function AnalyseText({ text }: { text: string }) {
  const zeilen = text.split("\n").map((z) => z.trim()).filter(Boolean);
  const absaetze = zeilen.filter((z) => !z.startsWith("- "));
  const vorschlaege = zeilen.filter((z) => z.startsWith("- ")).map((z) => z.slice(2));
  return (
    <div data-testid="lead-analyse-text">
      {absaetze.map((a, i) => (
        <p key={i} className={`${TEXT} mb-2`}>{a}</p>
      ))}
      {vorschlaege.length > 0 && (
        <ul className="mt-2 space-y-2">
          {vorschlaege.map((v, i) => (
            <li key={i} className={`flex gap-2.5 border-t border-[#f6f0e4] pt-2.5 dark:border-white/[0.06] ${TEXT}`}>
              <Icon name="bolt" className="mt-0.5 shrink-0 text-[18px] text-primary dark:text-[#f0b49b]" />
              <span>{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Stufe 4, Antwortzeiten — fehlt in Analysen, die vor der Ergänzung gespeichert wurden. */
function antwortText(a: EingangsAntwortzeit): string {
  const median = a.medianMinuten === null ? "" : `Antwort im Median nach ${antwortDauer(a.medianMinuten).text}, `;
  return `${median}${Math.round(a.anteilBinnen24h * 100)} % binnen 24 Std. beantwortet`;
}

function Zahlen({ s }: { s: EingangsStatistik }) {
  return (
    <details className="mt-4 rounded-xl border border-[#F0E7D6] px-4 py-3 dark:border-white/[0.06]" data-testid="lead-analyse-zahlen">
      <summary className="cursor-pointer font-body-sm text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
        Zahlen, auf die sich die Analyse stützt
      </summary>
      <p className={`mt-2 ${KLEIN}`}>{s.satz}{s.antwortzeit ? ` ${antwortText(s.antwortzeit)}.` : ""}</p>
      <ul className="mt-2 divide-y divide-[#F6F0E4] dark:divide-white/[0.05]">
        {s.zeilen.map((z) => (
          <li key={z.titel} className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-[1fr_auto] sm:gap-4">
            <span className={TEXT}>{z.titel}</span>
            <span className={`${KLEIN} tabular-nums sm:text-right`}>
              {z.anzahl} {z.anzahl === 1 ? "Anfrage" : "Anfragen"} · {z.angebote} mit Angebot · {z.gebucht} gebucht{z.antwortzeit ? ` · ${antwortText(z.antwortzeit)}` : ""}
            </span>
          </li>
        ))}
      </ul>
      <p className={`mt-2 ${KLEIN}`}>
        Daneben, keine Anfragen: {s.checkoutBestellungen} Checkout-Bestellungen, {s.vormerkungen} Vormerkungen. {s.hinweis}
      </p>
    </details>
  );
}

export function LeadAnalyseKarte({ zeitraum }: { zeitraum?: Zeitraum }) {
  const { data, loading, error, reload } = useApi<LeadAnalyseAntwort>("/api/lead-analyse");
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  async function erstellen() {
    setLaeuft(true);
    setFehler(null);
    setHinweis(null);
    try {
      const res = await apiFetch("/api/lead-analyse", { method: "POST", body: JSON.stringify(zeitraum ?? {}) });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: { vorhanden: boolean } };
      if (!body.data.vorhanden) setHinweis("Im gewählten Zeitraum ist keine Anfrage eingegangen — es gibt noch nichts zu analysieren.");
      reload();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  }

  const letzte = data?.letzte ?? null;

  return (
    <section
      className="rounded-[14px] border border-primary/25 bg-gradient-to-b from-[#FBF6EC] to-surface-container-lowest p-[18px] shadow-[0_8px_18px_-10px_rgba(45,38,26,0.14)] dark:from-white/[0.03] dark:to-night-card"
      data-testid="lead-analyse-karte"
    >
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-2 text-[16px] font-bold text-on-background dark:text-inverse-on-surface">
          <Icon name="auto_awesome" className="text-[18px] text-primary dark:text-[#f0b49b]" />
          Wie kommen mehr Anfragen rein?
        </h2>
        {data && (
          <span className={KLEIN} data-testid="lead-analyse-zeitpunkt">
            KI-Hinweise
            {letzte && ` · zuletzt am ${formatDateTime(letzte.erstelltAm)} ${letzte.ausloeser === "monatlich" ? "automatisch" : "per Knopf"} erzeugt`}
            {" · "}
            <span data-testid="lead-analyse-kosten">
              {money(data.kostenMonatCent)} von {money(data.deckelCent)} Monatsdeckel
            </span>
          </span>
        )}
      </div>

      {loading && <LoadingRows rows={3} />}
      {error && <ErrorNote message={error} onRetry={reload} />}

      {data && (
        <>
          {letzte?.status === "fertig" && letzte.text && <AnalyseText text={letzte.text} />}
          {letzte?.status === "verworfen" && (
            <p className={TEXT} data-testid="lead-analyse-verworfen">
              Die letzte Analyse haben wir nicht angezeigt: Sie enthielt eine Zahl, die sich nicht in deinen Daten belegen ließ.
            </p>
          )}
          {!letzte && <p className={TEXT}>Noch keine Analyse vorhanden.</p>}
          {letzte?.zahlen && <Zahlen s={letzte.zahlen} />}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button type="button" onClick={() => void erstellen()} disabled={laeuft || data.gesperrt} data-testid="lead-analyse-knopf">
              <Icon name={laeuft ? "progress_activity" : "refresh"} className={`text-[16px] ${laeuft ? "animate-spin" : ""}`} />
              {laeuft ? "Analyse läuft …" : letzte ? "Neu einschätzen" : "Einschätzen lassen"}
            </Button>
            <span className={KLEIN}>
              {zeitraum ? `Für ${zeitraumText(zeitraum)}. ` : ""}Jede Zahl im Text wird gegen deine Daten geprüft. Ist eine Zahl nicht belegt, wird
              der Text nicht angezeigt. Branchenrichtwerte (extern) erscheinen nur als Einordnung.
            </span>
          </div>
          {data.gesperrt && (
            <p className="mt-2 font-body-sm text-[12px] font-semibold text-secondary dark:text-[#b8ae9e]" data-testid="lead-analyse-gesperrt">
              Die Obergrenze von {money(data.deckelCent)} für diesen Monat ist erreicht. Ab dem nächsten Monat kannst du wieder analysieren.
            </p>
          )}
          {hinweis && <p className={`mt-2 ${KLEIN}`}>{hinweis}</p>}
          {fehler && (
            <div className="mt-3">
              <ErrorNote message={fehler} title="Analyse fehlgeschlagen" />
            </div>
          )}
        </>
      )}
    </section>
  );
}
